import * as yup from 'yup'
import { Box, Grid } from '@material-ui/core'
import { Form, Formik } from 'formik'
import React, { useMemo, useState } from 'react'
import { useTranslation } from '@opus/web.core.hooks.use-translation'
import { observer } from '~/common/mobx.decorator'
import { FormMode } from '@opus/web.core.form.utils'
import { FileUploadField } from '~/components/fields'
import WrapperUploadBox from '~/components/wrapper-upload-box'
import { additionalInfoApplyDialogStore } from '~/components/additional-info-apply-dialog/additional-info-apply-dialog.store'
import { Button } from '~/components/button'
import { OnValueChange } from '~/components/form'
import { quickApplyStore } from '~/features/quick-apply/quick-apply.store'

const ExperienceViewForm = observer(({ initialValues = {}, onSubmit, isLastStep }) => {
	const { t } = useTranslation()
	const { syncStepData } = additionalInfoApplyDialogStore
	const { currentJob } = quickApplyStore

	// Create validation schema with context
	const validationSchema = useMemo(() => {
		return yup.object().shape({
			resumes: yup.array().test({
				message: 'Please upload your resume to proceed',
				test: (arr) => {
					const isRequiredResume = currentJob?.isRequiredResume

					if (!isRequiredResume) {
						return true
					}

					return arr.length > 0 && arr.filter((item) => !item._destroy).length !== 0
				},
			}),
		})
		// eslint-disable-next-line
	}, [currentJob?.isRequiredResume])

	return (
		<Formik enableReinitialize={true} initialValues={initialValues} validateOnMount={true} validationSchema={validationSchema} onSubmit={onSubmit}>
			{({ handleSubmit, values, errors }) => {
				return (
					<Form id="experience-form" onSubmit={handleSubmit}>
						<OnValueChange onChange={({ values, dirty }) => syncStepData('resumes', values?.resumes, dirty)} />
						<Box mt={'10px'} mb={'20px'} fontWeight={500} fontSize={12} color="textSecondary">
							{currentJob?.isRequiredResume ? t('RESUME_REQUIRED_DESC') : t('RESUME_NOT_REQUIRED_DESC')}
						</Box>
						<ResumeSection values={values} />
						<Grid container spacing={2}>
							<Grid style={{ marginTop: 20, marginBottom: 20 }} item justify="space-between" container>
								<Grid item xs={12} style={{ paddingLeft: 4 }}>
									<Button form="experience-form" type="submit" fullWidth variant="contained" color="primary">
										{isLastStep ? t('COMPLETE') : t('NEXT_STEP')}
									</Button>
								</Grid>
							</Grid>
						</Grid>
					</Form>
				)
			}}
		</Formik>
	)
})

const ExperienceSection = observer(({ onNextTab, isLastStep }) => {
	const { handleSubmitStepData, stepData } = additionalInfoApplyDialogStore
	const initialValues = useMemo(
		() => ({
			resumes: stepData?.['resumes']?.filter((item) => item?.hasOwnProperty('_destroy') === false) || [],
		}),

		// eslint-disable-next-line
		[stepData?.['resumes']]
	)

	return (
		<Box>
			<Grid item xs={12}>
				<ExperienceViewForm
					onSubmit={async (values) => {
						await handleSubmitStepData('resumes', values?.resumes)
					}}
					initialValues={initialValues}
					isLastStep={isLastStep}
				/>
			</Grid>
		</Box>
	)
})

const ResumeSection = observer((values, onSubmit) => {
	const { t } = useTranslation()
	const [mode] = useState(FormMode.View)
	const { handleRemoveResumeFile } = additionalInfoApplyDialogStore

	return (
		<WrapperUploadBox hideOption={true} mode={mode} header={`${t('RESUME')}`} onEdit={() => {}} onRemove={() => {}} hasBorder>
			<Grid item>
				<FileUploadField
					callBackRemove={async (file) => {
						handleRemoveResumeFile(file)
					}}
					hideDelete={false}
					mode={'edit'}
					onlyPdf={false}
					name="resumes"
					title="UPLOAD_RESUME"
					subTitle={'ALLOW_UPLOAD_TYPE'}
					multiple={false}
					accept=".docx ,.png,.jpg,.jpeg,.pdf ,.heic ,.doc, .gif, .xls, .xlsx, .txt, .msg"
					message="$MESSAGES.MAKE_SURE_RESUME"
				/>
			</Grid>
		</WrapperUploadBox>
	)
})

export const WorkTab = observer(({ onNextTab, isLastStep }) => {
	return (
		<Box p={2}>
			<ExperienceSection onNextTab={onNextTab} isLastStep={isLastStep} />
		</Box>
	)
})
